/**
 * The announcement bar (sections/pruvit-announcements.liquid).
 *
 * What it does, and the accessibility rule behind most of it: anything that
 * moves on its own for more than five seconds needs a way to pause it
 * (WCAG 2.2.2). So:
 *
 *   - Rotation. More than one announcement rotates every data-rotate seconds.
 *     It pauses while the pointer is over the bar or focus is inside it, and
 *     the pause button stops it until pressed again. Next is always offered
 *     when there is more than one, so nobody depends on the timer.
 *   - Scrolling text. A message wider than its line scrolls as a loop -- Craig,
 *     2026-10-14, on a phone, instead of an ellipsis. Only when it overflows,
 *     and an announcement that is scrolling is not rotated away mid-sentence:
 *     the next one waits for the loop to come round.
 *   - Reduced motion. Nothing moves on its own: no rotation, no scrolling, the
 *     ellipsis comes back, and Next is the way through.
 *   - Countdown. Live, to the second; at zero the announcement goes.
 *   - Close. Remembered per announcement in localStorage; the pre-paint script
 *     in the section keeps a closed one from showing on the next page.
 */
(() => {
  const bar = document.querySelector('[data-pruvit-ann]');
  if (!bar || bar.hidden) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // A phone. Measured at 375, 2026-10-14: an announcement with a countdown, a
  // button and three controls left its message 87px -- a dozen characters of
  // scrolling text at a time. On a phone the countdown drops its seconds
  // while there are days left, and Next goes unless motion is reduced (the
  // bar rotates by itself, and Pause stays).
  const narrow = window.matchMedia('(max-width: 749px)');
  const rotateMs = Math.max(4, parseFloat(bar.dataset.rotate) || 7) * 1000;
  const daysTpl = bar.dataset.days || '%COUNT%d';
  const hoursTpl = bar.dataset.hours || '%COUNT%h';
  const SPEED = 40; // px per second for scrolling text

  const items = () => [...bar.querySelectorAll('[data-pruvit-ann-item]')];
  let index = Math.max(0, items().findIndex((el) => !el.hidden));
  let timer = null;
  let held = false; // pointer or focus inside
  let paused = reduced.matches; // the button, or reduced motion
  let looping = false; // the current message is scrolling
  let due = false; // rotation time has passed, waiting for the loop

  const current = () => items()[index] || null;

  // ---- scrolling text ---------------------------------------------------
  // Each message's own markup, kept from before the first loop was built.
  // The loop moves the text inside two copies, so "remove the copies" also
  // removed the message: on a phone, scrolling hides the address bar, that
  // fires resize, and the re-measure left only the button and the countdown
  // (Craig, 2026-10-17; reproduced at 375 with one resize event). The same
  // happened whenever rotation came back to a message that had scrolled.
  const source = new WeakMap();

  const setupMarquee = (item) => {
    const box = item.querySelector('[data-pruvit-ann-msg]');
    const text = item.querySelector('[data-pruvit-ann-text]');
    if (!box || !text) return false;
    // Back to the original before measuring.
    if (!source.has(text)) source.set(text, text.innerHTML);
    else text.innerHTML = source.get(text);
    box.classList.remove('is-marquee');
    text.style.removeProperty('--ann-dur');
    text.style.removeProperty('--ann-shift');
    if (reduced.matches) return false;
    // The box, not the text: the text is an inline span, and an inline
    // element's scrollWidth is always 0 -- measured, the first version never
    // detected an overflow. The box clips with nowrap, so its scrollWidth is
    // the line's full width.
    if (box.scrollWidth <= box.clientWidth + 1) return false;

    // Two copies side by side, moved left by exactly one copy and its gap, so
    // the loop joins without a jump. The copy is hidden from screen readers.
    const copy = document.createElement('span');
    copy.setAttribute('data-copy', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.innerHTML = text.innerHTML;
    copy.querySelectorAll('a, button').forEach((n) => n.setAttribute('tabindex', '-1'));
    const first = document.createElement('span');
    first.setAttribute('data-copy', '');
    first.innerHTML = text.innerHTML;
    text.replaceChildren(first, copy);
    box.classList.add('is-marquee');
    const width = first.getBoundingClientRect().width;
    const gap = 48;
    text.style.setProperty('--ann-shift', `-${width + gap}px`);
    text.style.setProperty('--ann-dur', `${((width + gap) / SPEED).toFixed(2)}s`);
    return true;
  };

  // ---- rotation -----------------------------------------------------------
  const animating = () => items().length > 1 || looping;

  const syncControls = () => {
    const many = items().length > 1;
    items().forEach((item) => {
      const next = item.querySelector('[data-pruvit-ann-next]');
      const pause = item.querySelector('[data-pruvit-ann-pause]');
      if (next) next.hidden = !many || (narrow.matches && !reduced.matches);
      if (pause) {
        pause.hidden = reduced.matches || !animating();
        pause.setAttribute('aria-label', paused ? pause.dataset.labelPlay : pause.dataset.labelPause);
        pause.setAttribute('aria-pressed', paused ? 'true' : 'false');
      }
    });
    bar.classList.toggle('is-paused', paused);
    bar.classList.toggle('is-held', held);
  };

  const schedule = () => {
    clearTimeout(timer);
    due = false;
    if (items().length < 2 || paused || held) return;
    timer = setTimeout(() => {
      due = true;
      if (!looping) advance();
    }, rotateMs);
  };

  const show = (i) => {
    const list = items();
    if (!list.length) {
      bar.hidden = true;
      return;
    }
    index = ((i % list.length) + list.length) % list.length;
    list.forEach((el, k) => {
      el.hidden = k !== index;
    });
    looping = setupMarquee(list[index]);
    tick();
    syncControls();
    schedule();
  };

  const advance = () => show(index + 1);

  // Rotation waits for a scrolling message to complete a pass before moving
  // on. One delegated listener; per-item ones would pile up on every show().
  bar.addEventListener('animationiteration', (event) => {
    if (due && event.target.matches('[data-pruvit-ann-text]')) advance();
  });

  // ---- countdown ----------------------------------------------------------
  const pad = (n) => String(n).padStart(2, '0');
  const tick = () => {
    const now = Date.now() / 1000;
    let removed = false;
    items().forEach((item) => {
      const end = parseFloat(item.dataset.end || '0');
      if (end && end <= now) {
        item.remove();
        removed = true;
        return;
      }
      const cd = item.querySelector('[data-pruvit-ann-cd-v]');
      if (!cd || !end) return;
      let left = Math.max(0, Math.floor(end - now));
      const d = Math.floor(left / 86400);
      left -= d * 86400;
      const h = Math.floor(left / 3600);
      left -= h * 3600;
      const m = Math.floor(left / 60);
      const s = left - m * 60;
      const days = d > 0 ? daysTpl.replace('%COUNT%', d) + ' ' : '';
      cd.textContent =
        d > 0 && narrow.matches ? days + hoursTpl.replace('%COUNT%', h) : `${days}${pad(h)}:${pad(m)}:${pad(s)}`;
    });
    if (removed) show(index);
  };
  setInterval(tick, 1000);

  // ---- controls -----------------------------------------------------------
  bar.addEventListener('click', (event) => {
    if (event.target.closest('[data-pruvit-ann-next]')) {
      advance();
      return;
    }
    if (event.target.closest('[data-pruvit-ann-pause]')) {
      paused = !paused;
      syncControls();
      schedule();
      return;
    }
    const close = event.target.closest('[data-pruvit-ann-close]');
    if (close) {
      const item = close.closest('[data-pruvit-ann-item]');
      try {
        const closed = JSON.parse(localStorage.getItem('pruvit-ann-closed') || '[]');
        if (!closed.includes(item.dataset.id)) closed.push(item.dataset.id);
        localStorage.setItem('pruvit-ann-closed', JSON.stringify(closed.slice(-50)));
      } catch (e) {
        // Closed for this page view at least.
      }
      item.remove();
      show(index);
      // The button that had focus is gone. Keep a keyboard user in place: the
      // next announcement's first control, or the page if the bar is empty.
      // Measured: focus otherwise fell to the cookie banner.
      const target = bar.hidden
        ? document.getElementById('MainContent')
        : current()?.querySelector('a, button:not([hidden])');
      if (target) {
        if (target.id === 'MainContent') target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }
  });

  const hold = (on) => {
    held = on;
    syncControls();
    schedule();
  };
  bar.addEventListener('pointerenter', () => hold(true));
  bar.addEventListener('pointerleave', () => hold(false));
  bar.addEventListener('focusin', () => hold(true));
  bar.addEventListener('focusout', (e) => {
    if (!bar.contains(e.relatedTarget)) hold(false);
  });

  reduced.addEventListener?.('change', () => {
    paused = reduced.matches;
    show(index);
  });

  // A resize can make a message fit, or stop fitting -- but only a change of
  // width can. A phone's address bar showing and hiding on scroll changes the
  // height alone, and re-measuring then restarted the loop mid-sentence.
  let resizeTimer = null;
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => show(index), 200);
  });

  // Fonts change widths; measure once they are in.
  (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(() => show(index));
})();
